import React, { useRef } from 'react';
import { Dayjs } from 'dayjs';
import { SensorDescriptor } from '@dnd-kit/core';
import { SegmentedAppointment } from '../segments';
import { TechLane } from '../useSchedulingData';
import { ScheduleException } from '../../../services/scheduleApi';
import { DayGrid } from './DayGrid';
import { DropIntent } from './WeekGrid';

export interface MobileWeekProps {
  weekStart: Dayjs;
  selectedDate: Dayjs;
  onSelectDate: (d: Dayjs) => void;
  appts: SegmentedAppointment[];
  techs: TechLane[];
  exceptions: ScheduleException[];
  onOpen: (id: number) => void;
  onDrop?: (e: DropIntent) => void;
  sensors?: SensorDescriptor<any>[];
}

const DAY_COUNT = 7;
// A swipe shorter than this reads as a scroll/tap, not a day-change gesture —
// mirrors the threshold CalendarPage uses for its own day-view swipe.
const SWIPE_THRESHOLD = 60;

export function MobileWeek({
  weekStart,
  selectedDate,
  onSelectDate,
  appts,
  techs,
  exceptions,
  onOpen,
  onDrop,
  sensors,
}: MobileWeekProps) {
  const days = Array.from({ length: DAY_COUNT }, (_, i) => weekStart.add(i, 'day'));
  const selectedDateStr = selectedDate.format('YYYY-MM-DD');

  const countByDate = new Map<string, number>();
  for (const appt of appts) {
    const datesForAppt = Array.from(new Set(appt.segments.map((s) => s.date)));
    for (const d of datesForAppt) {
      countByDate.set(d, (countByDate.get(d) || 0) + 1);
    }
  }

  const gridAreaRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartScrollLeft = useRef<number | null>(null);
  // Flipped true whenever DayGrid commits a drag-drop during the current
  // touch gesture. A committed drop is an edit, not a swipe — dnd-kit's own
  // touchend listener is attached directly to the dragged element, so it
  // fires (and calls onDrop, which sets this ref) before the wrapper's
  // React-delegated touchend handler below runs.
  const dropHandledRef = useRef(false);
  const handleDrop = (e: DropIntent) => {
    dropHandledRef.current = true;
    onDrop?.(e);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    // clientY may be omitted by callers/tests that don't care about the
    // vertical component — treat that as "no vertical movement" rather than
    // letting it poison the dominant-horizontal comparison below with NaN.
    touchStartY.current = e.touches[0].clientY ?? 0;
    dropHandledRef.current = false;
    const laneRow = gridAreaRef.current?.querySelector<HTMLElement>('[data-testid="day-grid"]');
    touchStartScrollLeft.current = laneRow ? laneRow.scrollLeft : null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    const startScrollLeft = touchStartScrollLeft.current;
    const dropHandled = dropHandledRef.current;
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartScrollLeft.current = null;
    dropHandledRef.current = false;

    if (startX === null) return;
    // A drag-drop just completed as part of this gesture — the position
    // change is the intended edit, not a request to change days.
    if (dropHandled) return;

    // The lane row (DayGrid's own overflow-x:auto wrapper, 2+ tech lanes)
    // actually scrolled during this gesture — that's a lane-scroll, not a
    // day-swipe.
    const laneRow = gridAreaRef.current?.querySelector<HTMLElement>('[data-testid="day-grid"]');
    if (laneRow && startScrollLeft !== null && laneRow.scrollLeft !== startScrollLeft) return;

    const dx = e.changedTouches[0].clientX - startX;
    const dy = startY !== null ? (e.changedTouches[0].clientY ?? 0) - startY : 0;
    // Require a dominant-horizontal gesture so a vertical time-scroll that
    // happens to cross 60px horizontally doesn't get read as a day-swipe.
    const isDaySwipe = Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > 1.5 * Math.abs(dy);
    if (isDaySwipe) {
      onSelectDate(selectedDate.add(dx < 0 ? 1 : -1, 'day'));
    }
  };

  return (
    <div data-testid="mobile-week">
      <div
        data-testid="mobile-week-strip"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          gap: 4,
          padding: '8px 4px',
          overflowX: 'auto',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {days.map((day) => {
          const dateStr = day.format('YYYY-MM-DD');
          const isSelected = dateStr === selectedDateStr;
          const count = countByDate.get(dateStr) || 0;
          return (
            <button
              key={dateStr}
              type="button"
              aria-label={day.format('ddd D')}
              aria-pressed={isSelected}
              data-testid={`mobile-week-day-${dateStr}`}
              onClick={() => onSelectDate(day)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                flex: '1 0 auto',
                minWidth: 44,
                padding: '6px 8px',
                borderRadius: 8,
                border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb',
                backgroundColor: isSelected ? '#eff6ff' : '#fff',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>{day.format('dd')}</span>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{day.format('D')}</span>
              {count > 0 && (
                <span
                  data-testid={`mobile-week-count-${dateStr}`}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: isSelected ? '#2563eb' : '#94a3b8',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div
        ref={gridAreaRef}
        data-testid="mobile-week-grid-area"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <DayGrid
          date={selectedDate}
          appts={appts}
          techs={techs}
          exceptions={exceptions}
          onOpen={onOpen}
          onDrop={handleDrop}
          sensors={sensors}
        />
      </div>
    </div>
  );
}

export default MobileWeek;
